import * as React from 'react';

import { DomElement, domToReact, HTMLReactParserOptions, htmlToDOM } from 'html-react-parser';
import { Carousel } from 'react-responsive-carousel';
import classNames from 'classnames';

import './styles.scss';
import { includes, uniqueId } from 'lodash';
import Icon from '../Icon';
import Overlay from '../Overlay';


export interface IRenderGalleryProps {
    /* DOM to parse */
    dom?: string;
    /** Unsafe config */
    config?: any;
    /** External overlay status */
    overlayOpen?: boolean;
    /** External overlay opening */
    openOverlay?: () => void;
    /** External overlay closing */
    closeOverlay?: () => void;
    /** Children prepared elsewhere */
    preparedChildren?: DomElement[];
}

/**
 * Renders DOM string to gallery. Designed for HTML hydration.
 */
const RenderGallery: React.FunctionComponent<IRenderGalleryProps> = (props) => {

    const images: React.ReactElement[] = [];
    const [overlayOpen, setOverlayOpen] = React.useState<boolean>(false);
    const [currentImageIndex, setCurrentImageIndex] = React.useState<number>(0);
    const parserOptions: HTMLReactParserOptions = {
        replace: (domNode: any) => {
            if (domNode.name) {
                if (domNode.name === 'div') {
                    const { class: className, ...rest } = domNode.attribs;

                    if (includes(className, 'gallery__slide')) {
                        return (
                            <React.Fragment/>
                        );
                    }
                    if (includes(className, 'gallery')) {
                        [...domToReact(domNode.children, {})].map((i) => {
                            images.push(i);
                        });
                        return (
                            <React.Fragment/>
                        );
                    }
                }
            } else {
                return (
                    <React.Fragment/>
                );
            }
            return false;
        },
    };

    // Main output

    const domTree = htmlToDOM(props.dom ? props.dom.replace(/>\s+</g, '><') : '');

    domToReact(props.preparedChildren || domTree, parserOptions);

    return (
        <React.Fragment>
            <div className={classNames('gallery', {
                'has-thumbs': !!props.config?.showThumbs,
                'has-overlay': !!props.config?.hasOverlay,
            })}>
                <Carousel
                    showStatus={false}
                    showThumbs={!!props.config?.showThumbs || false}
                    swipeable={true}
                    showArrows={images.length > 1 ? (typeof props.config?.showArrows !== 'undefined' ? props.config.showArrows : true) : false}
                    showIndicators={images.length > 1 ? (typeof props.config?.showIndicators !== 'undefined' ? props.config.showIndicators : true) : false}
                    autoPlay={false}
                    emulateTouch={true}
                    useKeyboardArrows={true}
                    preventMovementUntilSwipeScrollTolerance={true}
                    swipeScrollTolerance={50}
                    renderArrowNext={(onClick) => {
                        if (images.length > 1 ? (typeof props.config?.showArrows !== 'undefined' ? props.config.showArrows : true) : false) {
                            return (
                                <button onClick={onClick} className="control-arrow control-next"><Icon kind="arrow2-right" width={12} height={12}/></button>
                            );
                        }
                        return (
                            <React.Fragment/>
                        );
                    }}
                    renderArrowPrev={(onClick) => {
                        if (images.length > 1 ? (typeof props.config?.showArrows !== 'undefined' ? props.config.showArrows : true) : false) {
                            return (
                                <button onClick={onClick} className="control-arrow control-prev"><Icon kind="arrow2-left" width={12} height={12}/></button>
                            );
                        }
                        return (
                            <React.Fragment/>
                        );
                    }}
                    renderThumbs={!!props.config?.showThumbs ? (thumbs) => {
                        return thumbs.map((item, i) => {
                            if (typeof item === 'object' && item.props['data-thumb']) {
                                const url = item.props['data-thumb'];
                                return (<img alt={item.props['data-title']} key={i} src={url}/>);
                            } else {
                                return (
                                    <React.Fragment key={i}>
                                        {item}
                                    </React.Fragment>
                                );
                            }
                        });
                    } : undefined}
                    onClickItem={!!props.config?.hasOverlay ? (index) => {
                        if (props.openOverlay) {
                            props.openOverlay();
                        } else {
                            setOverlayOpen(true);
                            setCurrentImageIndex(index);
                        }
                    } : undefined}
                >
                    {images.map((image, imageIndex) => {
                        return (
                            <React.Fragment key={imageIndex}>
                                {image}
                            </React.Fragment>
                        );
                    })}
                </Carousel>
            </div>

            {!!props.config?.hasOverlay && (
                <Overlay
                    isOpen={props.overlayOpen || overlayOpen}
                    doClose={() => {
                        if (props.closeOverlay) {
                            props.closeOverlay();
                        } else {
                            setOverlayOpen(false);
                        }
                    }}
                    layout="focusview"
                    size="max"
                    canEscapeKeyClose={typeof props.config?.canEscapeKeyClose !== 'undefined' ? props.config.canEscapeKeyClose : true}
                    className="gallery-overlay"
                    customHeader={(
                        <button className="gallery-overlay__close" onClick={() => {
                            if (props.closeOverlay) {
                                props.closeOverlay();
                            } else {
                                setOverlayOpen(false);
                            }
                        }}>
                            <Icon kind="close" width={16} height={16}/>
                        </button>
                    )}
                    zIndex={16000100}
                >
                    <div className="gallery-overlay__content">
                        <Carousel
                            showStatus={false}
                            showThumbs={!!props.config?.showThumbs || false}
                            swipeable={true}
                            showArrows={images.length > 1 ? (typeof props.config?.showArrows !== 'undefined' ? props.config.showArrows : true) : false}
                            showIndicators={images.length > 1 ? (typeof props.config?.showIndicators !== 'undefined' ? props.config.showIndicators : true) : false}
                            autoPlay={false}
                            selectedItem={currentImageIndex}
                            emulateTouch={true}
                            useKeyboardArrows={true}
                            renderArrowNext={(onClick) => {
                                if (images.length > 1 ? (typeof props.config?.showArrows !== 'undefined' ? props.config.showArrows : true) : false) {
                                    return (
                                        <button onClick={onClick} className="control-arrow control-next"><Icon kind="arrow2-right" width={12} height={12}/></button>
                                    );
                                }
                                return (
                                    <React.Fragment/>
                                );
                            }}
                            renderArrowPrev={(onClick) => {
                                if (images.length > 1 ? (typeof props.config?.showArrows !== 'undefined' ? props.config.showArrows : true) : false) {
                                    return (
                                        <button onClick={onClick} className="control-arrow control-prev"><Icon kind="arrow2-left" width={12} height={12}/></button>
                                    );
                                }
                                return (
                                    <React.Fragment/>
                                );
                            }}
                            renderThumbs={!!props.config?.showThumbs ? (thumbs) => {
                                return thumbs.map((item, i) => {
                                    if (typeof item === 'object' && item.props['data-thumb']) {
                                        const url = item.props['data-thumb'];
                                        return (<img alt={item.props['data-title']} key={i} src={url}/>);
                                    } else {
                                        return (
                                            <React.Fragment key={i}>
                                                {item}
                                            </React.Fragment>
                                        );
                                    }
                                });
                            } : undefined}
                        >
                            {images.map((item, i) => {
                                if (typeof item === 'object' && item.props['data-full'] || item.props['data-full-srcset']) {
                                    const thumbUrl = item.props['data-thumb'];
                                    const url = item.props['data-url'] ?? item.props.src;
                                    if (item.props['data-full-srcset']){
                                        let srcset;
                                        try {
                                            srcset = JSON.parse(item.props['data-full-srcset']);
                                        } catch (e) {}
                                        if(typeof srcset === 'object') {
                                            const sizes = Object.keys(srcset);
                                            return (
                                                <picture className="gallery__slide" data-thumb={thumbUrl} key={i}>
                                                    {sizes.map((size, index) => {
                                                        if (index + 1 === sizes.length) {
                                                            return (<img key={index} src={srcset[size]} alt={item.props['data-title']}/>);
                                                        } else {
                                                            return (<source key={index} srcSet={srcset[size]} media={size}/>);
                                                        }
                                                    })}
                                                </picture>
                                            );
                                        }
                                    }
                                    return (<img className="gallery__slide" data-thumb={thumbUrl} alt={item.props['data-title']} key={i} src={url}/>);
                                } else {
                                    return (
                                        <React.Fragment key={i}>
                                            {item}
                                        </React.Fragment>
                                    );
                                }
                            })}
                        </Carousel>
                    </div>
                </Overlay>
            )}
        </React.Fragment>
    );
};

RenderGallery.displayName = 'RenderGallery';

export default RenderGallery;
